// ** React Imports **
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

// ** Third Party Imports **
import { SkeletonLoading } from '@saleshandy/design-system';
import { Search } from '@saleshandy/icons';
import axios from 'axios';
import { debounce } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';

// ** Custom Imports **
import { getContactsListForManualCallDialingRequest } from '../../../../components/dialer/extra-actions';
import { DialerCall } from '../../../svg/dialer-call';
import Spinner, {
  SpinnerAnimation,
} from '../../../design-system/components/atoms/spinner/spinner';
import type { DialerPhoneNumberOption, ProspectOption } from '../types';
import { RootState } from '../../../../store/root-reducer';
import { executeOnRequestStatus, getIsRequestPending } from '../../../utils';
import {
  resetGetContactsListForManualCallDialingRequest,
  resetProspectMinimalListResponse,
} from '../../../../components/dialer/dialer-slice';
import { ExceptionTypes } from '../../../types';
import { getInitials, getPhoneCountAndFirst } from '../utils/helper';
import DialerControlsHeader from './dialer-controls-header';
import { DialerCallSourceEnum } from '../../../../components/dialer/types/call-logs';

interface DialerProspectProps {
  onProspectCall: (requestedPhonenumber: DialerPhoneNumberOption) => void;
  onProspectSelected?: (prospect: ProspectOption) => void;
  onTogglePhoneNumbersPickerSheet?: (prospect: ProspectOption) => void;
}

const DialerProspect: React.FC<DialerProspectProps> = ({
  onProspectCall = () => {},
  onProspectSelected = () => {},
  onTogglePhoneNumbersPickerSheet = () => {},
}) => {
  // ** hooks **
  const dispatch = useDispatch();
  const getContactsListForManualCallDialingRequestStatus = useSelector(
    (state: RootState) =>
      state.dialer.getContactsListForManualCallDialingRequest.status,
  );
  const prospectMinimalListResponse = useSelector(
    (state: RootState) => state.dialer.prospectMinimalListResponse,
  );

  // ** States **
  const [prospects, setProspects] = useState<ProspectOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [requestOptions, setRequestOptions] = useState({
    search: '',
    pageNum: 1,
    pageSize: 25,
    totalElements: 0,
  });
  const lastSearchTermRef = useRef<string>('');
  const contactsListCancelTokenRef = useRef<ReturnType<
    typeof axios.CancelToken.source
  > | null>(null);

  const hasMore = prospects.length < requestOptions.totalElements;

  const isInitialLoad =
    requestOptions.pageNum === 1 &&
    getIsRequestPending(getContactsListForManualCallDialingRequestStatus) &&
    prospects.length === 0;

  // ** Handlers **
  const dispatchGetContactsList = useCallback(
    (payload: { search?: string; pageNum: number; pageSize: number }) => {
      if (contactsListCancelTokenRef.current) {
        contactsListCancelTokenRef.current.cancel({
          type: ExceptionTypes.Silent,
          message: 'DIALER_CONTACTS_LIST_CANCELLED',
        } as any);
        contactsListCancelTokenRef.current = null;
      }
      contactsListCancelTokenRef.current = axios.CancelToken.source();
      const cancelToken = contactsListCancelTokenRef.current.token;
      dispatch(
        getContactsListForManualCallDialingRequest({
          ...payload,
          cancelToken,
        }),
      );
    },
    [dispatch],
  );

  const handleGetProspectsList = useCallback(() => {
    const { search, pageNum, pageSize } = requestOptions;
    dispatchGetContactsList({ search, pageNum, pageSize });
  }, [
    requestOptions?.search,
    requestOptions?.pageNum,
    requestOptions?.pageSize,
    dispatchGetContactsList,
  ]);

  const fetchNextProspects = useCallback(() => {
    const nextPage = requestOptions.pageNum + 1;
    setRequestOptions((prev) => ({ ...prev, pageNum: nextPage }));
    dispatchGetContactsList({
      search: requestOptions.search,
      pageNum: nextPage,
      pageSize: requestOptions.pageSize,
    });
  }, [requestOptions, dispatchGetContactsList]);

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      const trimmed = term?.trim();
      if (trimmed === lastSearchTermRef?.current) return;
      lastSearchTermRef.current = trimmed;
      setProspects([]);
      setRequestOptions((prev) => ({
        ...prev,
        search: trimmed,
        pageNum: 1,
        totalElements: 0,
      }));
      dispatchGetContactsList({
        search: trimmed,
        pageNum: 1,
        pageSize: requestOptions?.pageSize,
      });
    }, 600),
    [dispatchGetContactsList, requestOptions?.pageSize],
  );

  const handleProspectCall = useCallback(
    (prospect: ProspectOption) => {
      const isPrimaryProspect =
        prospect?.phoneNumbers?.find((item) => item?.isPrimary === true) ||
        prospect?.phoneNumbers?.[0];

      const requestedPhonenumber = {
        callingCode: isPrimaryProspect?.callingCode || '',
        number: isPrimaryProspect?.number || '',
        phoneNumber: isPrimaryProspect?.phoneNumber || '',
        callSource: DialerCallSourceEnum.Manual,
      };
      onProspectSelected(prospect);
      onProspectCall(requestedPhonenumber);
    },
    [onProspectCall, onProspectSelected],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // ** Effects **
  useEffect(() => {
    executeOnRequestStatus({
      status: getContactsListForManualCallDialingRequestStatus,
      onSuccess: () => {
        const newResults = prospectMinimalListResponse?.results || [];
        if (prospectMinimalListResponse?.pageNum === 1) {
          setProspects(newResults);
        } else {
          setProspects((prev) => [...prev, ...newResults]);
        }
        setRequestOptions((prev) => ({
          ...prev,
          totalElements: prospectMinimalListResponse?.totalCount ?? 0,
        }));
      },
      onFailed: () => {
        setProspects([]);
      },
    });
  }, [
    getContactsListForManualCallDialingRequestStatus,
    prospectMinimalListResponse,
  ]);

  useEffect(() => {
    // Fetch prospects on mount
    handleGetProspectsList();
    return () => {
      if (contactsListCancelTokenRef.current) {
        contactsListCancelTokenRef.current.cancel({
          type: ExceptionTypes.Silent,
          message: 'DIALER_CONTACTS_LIST_CANCELLED',
        } as any);
        contactsListCancelTokenRef.current = null;
      }
      dispatch(resetGetContactsListForManualCallDialingRequest());
      dispatch(resetProspectMinimalListResponse());
    };
  }, []);

  // ** Render **
  return (
    <div className="dialer-prospect">
      {/* Search Input */}
      <div className="dialer-search-container">
        <Search width={14} height={14} className="dialer-search-icon" />
        <input
          type="text"
          className="dialer-search-input"
          placeholder="Search contacts"
          value={searchTerm}
          onChange={(e) => {
            const { value } = e.target;
            setSearchTerm(value);
            debouncedSearch(value);
          }}
        />
      </div>

      {/* Prospect List */}
      <div className="dialer-prospect-list" id="dialer-prospect-scrollable">
        {isInitialLoad ? (
          <div className="dialer-prospect-skeleton">
            {[...Array(6)].map((_, i) => (
              <div
                key={`dialer-prospect-skeleton-${i + 1}`}
                className="dialer-prospect-item"
              >
                <SkeletonLoading
                  width={24}
                  height={24}
                  style={{ borderRadius: '50%' }}
                />
                <SkeletonLoading width={120 + (i % 3) * 30} height={14} />
              </div>
            ))}
          </div>
        ) : (
          <InfiniteScroll
            dataLength={prospects?.length || 0}
            next={fetchNextProspects}
            hasMore={hasMore}
            loader={
              <div className="dialer-prospect-loader">
                <Spinner animation={SpinnerAnimation.Border} />
              </div>
            }
            scrollableTarget="dialer-prospect-scrollable"
            endMessage={
              prospects?.length > 0 ? null : (
                <div className="dialer-prospect-empty-state">
                  <p className="dialer-prospect-empty-state-text">
                    {requestOptions.search
                      ? 'No prospect found for this search'
                      : 'No contacts available'}
                  </p>
                </div>
              )
            }
          >
            {prospects?.map((prospect) => {
              const prospectName =
                `${prospect?.firstName?.trim() || ''} ${
                  prospect?.lastName?.trim() || ''
                }`?.trim() || 'Unknown Prospect';

              const {
                count: phoneCount,
                first: firstPhone,
              } = getPhoneCountAndFirst(prospect?.phoneNumbers);
              const extraCount = Math.max(0, phoneCount - 1);

              return (
                <div key={prospect?.id} className="dialer-prospect-item">
                  <DialerControlsHeader
                    avatarContent={getInitials(prospectName)}
                    name={
                      prospectName === 'Unknown Prospect'
                        ? firstPhone
                        : prospectName
                    }
                    phone={firstPhone}
                    icon={
                      <DialerCall
                        width={16}
                        height={16}
                        className="dialer-prospect-call-icon"
                      />
                    }
                    onIconClick={() => handleProspectCall(prospect)}
                    extraPhoneCount={extraCount}
                    onMoreClick={
                      extraCount > 0 && onTogglePhoneNumbersPickerSheet
                        ? () => onTogglePhoneNumbersPickerSheet(prospect)
                        : undefined
                    }
                  />
                </div>
              );
            })}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};

export default memo(DialerProspect);
